import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { ModelId } from '@portal-core/data/common/types/mc-model.type';
import { LicenseStorageService } from '@portal-core/license-storage/services/license-storage.service';
import { LicenseAdminSettings } from '@portal-core/licenses/models/license-admin-settings.model';
import { ContactInfo } from '@portal-core/licenses/models/license-contact-info.model';
import { LicenseCreateRequest } from '@portal-core/licenses/models/license-create-request.model';
import { LicenseOpenAISettings } from '@portal-core/licenses/models/license-open-ai-settings.model';
import { LicensePublicModel } from '@portal-core/licenses/models/license-public.model';
import { LicenseSamlAuthenticationSettingsSimple } from '@portal-core/licenses/models/license-saml-authentication-settings-simple.model';
import { LicenseSamlAuthenticationSettings } from '@portal-core/licenses/models/license-saml-authentication-settings.model';
import { LicenseSeatCounts } from '@portal-core/licenses/models/license-seat-counts.model';
import { SecuritySettings } from '@portal-core/licenses/models/license-security-settings.model';
import { LicenseSettings } from '@portal-core/licenses/models/license-settings.model';
import { SubscriptionAddition } from '@portal-core/licenses/models/license-subscription-addition.model';
import { SubscriptionOrderItem } from '@portal-core/licenses/models/license-subscription-order-item.model';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesSizeStorage } from '@portal-core/licenses/models/licenses-size-storage.model';
import { LicensesApiService } from '@portal-core/licenses/services/licenses-api.service';
import { LicensesDataService } from '@portal-core/licenses/services/licenses-data.service';
import { OpenAICompletion } from '@portal-core/project-files/models/open-ai/open-ai-completion.model';
import { UserBulkResponse } from '@portal-core/users/models/user-bulk-response.model';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { Observable, tap } from 'rxjs';

@Service()
@Resettable()
export class LicensesService extends CollectionServiceBase<License> {
  private readonly licensesApiService = inject(LicensesApiService);
  private readonly licenseStorageService = inject(LicenseStorageService);

  constructor() {
    super(inject(LicensesDataService));
  }

  protected override fetchItemById$(itemId: ModelId): Observable<License> {
    return this.licensesApiService.getLicenseById$(itemId);
  }

  createLicense$(licenseCreateRequest: LicenseCreateRequest): Observable<void> {
    return this.licensesApiService.createLicense$(licenseCreateRequest);
  }

  getLicensesForCurrentUser$(): Observable<License[]> {
    return this.licensesApiService.getLicensesForCurrentUser$().pipe(
      tap(licenses => this.addItems$(licenses))
    );
  }

  getLicenseDefaultTeams$(licenseId: number): Observable<number[]> {
    return this.licensesApiService.getLicenseDefaultTeams$(licenseId);
  }

  getLicenseByQuery$(filter: PageFilter): Observable<Page<License>> {
    return this.licensesApiService.getLicensesByQuery$(filter);
  }

  verifyBulkUserCsvFile$(licenseId: number, seatType: number, csvFile: FormData): Observable<UserBulkResponse> {
    return this.licensesApiService.verifyBulkUserCsvFile$(licenseId, seatType, csvFile);
  }

  setActivationStatus$(licenseId: number, status: number): Observable<void> {
    return this.licensesApiService.setActivationStatus$(licenseId, status).pipe(
      tap(() => this.updateItems$({ [licenseId]: { LicenseActivationStatus: status } }))
    );
  }

  changeMaxCountOfSeats$(licenseId: number, licensesSeats: LicenseSeatCounts): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeMaxCountOfSeats$(licenseId, licensesSeats);
  }

  changeMaxStorageSize$(licenseId: number, licenseSize: LicensesSizeStorage): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeMaxStorageSize$(licenseId, licenseSize);
  }

  changeAdminLicenseSettings$(licenseId: number, licenseData: LicenseAdminSettings, avatar: File): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeAdminLicenseSettings$(licenseId, licenseData, avatar).pipe(
      tap(() => {
        this.fetchItemById$(licenseId).subscribe(updatedLicense => this.updateItems$([updatedLicense]));
        this.licenseStorageService.refreshItemById(licenseId);
      })
    );
  }

  changeLicenseSettings$(licenseId: number, licenseData: LicenseSettings, avatar: File): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeLicenseSettings$(licenseId, licenseData, avatar).pipe(
      tap(() => {
        this.fetchItemById$(licenseId).subscribe(updatedLicense => this.updateItems$({
          [licenseId]: { KeyLabel: updatedLicense.KeyLabel, Avatar: updatedLicense.Avatar, VanityBaseUrl: updatedLicense.VanityBaseUrl, Description: updatedLicense.Description }
        }));
      })
    );
  }

  changeVanityUrl$(licenseId: number, vanityUrl: string): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeVanityUrl$(licenseId, vanityUrl).pipe(
      tap(() => {
        this.fetchItemById$(licenseId).subscribe(updatedLicense => this.updateItems$({
          [updatedLicense.Id]: { VanityBaseUrl: updatedLicense.VanityBaseUrl }
        }));
      })
    );
  }

  changeSecuritySettings$(licenseId: number, securitySettings: SecuritySettings): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeSecuritySettings$(licenseId, securitySettings).pipe(
      tap(() => {
        this.fetchItemById$(licenseId).subscribe(updatedLicense => this.updateItems$({
          [updatedLicense.Id]: {
            RequirePasswordChangeAfterDays: updatedLicense.RequirePasswordChangeAfterDays,
            MaxFailedAccessAttempts: updatedLicense.MaxFailedAccessAttempts,
            MaxIdleTimeMinutes: updatedLicense.MaxIdleTimeMinutes,
            PasswordMinimumLength: updatedLicense.PasswordMinimumLength
          }
        }));
      })
    );
  }

  getOpenAISettings$(licenseId: number): Observable<LicenseOpenAISettings> {
    return this.licensesApiService.getOpenAISettings$(licenseId);
  }

  changeOpenAISettings$(licenseId: number, apiKey: string, apiModel: string): Observable<MadCloudResult<void>> {
    return this.licensesApiService.changeOpenAISettings$(licenseId, apiKey, apiModel).pipe(
      tap(() => {
        this.updateItems$({ [licenseId]: { HasOpenAIKey: !!apiKey } });
      })
    );
  }

  verifyOpenAIKey$(apiKey: string, apiModel: string): Observable<MadCloudResult<OpenAICompletion>> {
    return this.licensesApiService.verifyOpenAIKey$(apiKey, apiModel);
  }

  emailMadCapSalesTeam$(licenseId: number, message: string): Observable<void> {
    return this.licensesApiService.emailMadCapSalesTeam$(licenseId, message);
  }

  calculateSubscriptionAdditions$(licenseId: number, additions: SubscriptionAddition): Observable<SubscriptionOrderItem[]> {
    return this.licensesApiService.calculateSubscriptionAdditions$(licenseId, additions);
  }

  processSubscriptionAdditions$(licenseId: number, additions: SubscriptionAddition): Observable<void> {
    return this.licensesApiService.processSubscriptionAdditions$(licenseId, additions).pipe(
      tap(() => {
        this.fetchItemById$(licenseId).subscribe(license => this.updateItems$([license]));
      })
    );
  }

  getContactInfo$(licenseId: number): Observable<ContactInfo> {
    return this.licensesApiService.getContactInfo$(licenseId).pipe(
      tap((contactInfo) => this.updateItems$({ [licenseId]: { ContactInfo: contactInfo } }))
    );
  }

  deleteContactInfo$(licenseId: number): Observable<void> {
    return this.licensesApiService.deleteContactInfo$(licenseId).pipe(
      tap(() => {
        this.updateItems$({ [licenseId]: { ContactInfo: { billingContact: null, shippingContact: null, creditCard: null } } });
      })
    );
  }

  updateContactInfo$(licenseId: number, contactInfoModel: ContactInfo): Observable<void> {
    return this.licensesApiService.updateContactInfo$(licenseId, contactInfoModel).pipe(
      tap(() => {
        this.licensesApiService.getContactInfo$(licenseId).subscribe(contactInfo => this.updateItems$({ [licenseId]: { ContactInfo: contactInfo } }));
      })
    );
  }

  validateUserEmailAddress$(licenseId: number, email: string): Observable<MadCloudResult<void>> {
    return this.licensesApiService.validateUserEmailAddress$(licenseId, email);
  }

  setSamlAuthenticationSettings$(licenseId: number, licenseSamlAuthenticationSettings: LicenseSamlAuthenticationSettings) {
    return this.licensesApiService.setSamlAuthenticationSettings$(licenseId, licenseSamlAuthenticationSettings);
  }

  getSamlAuthenticationSettings$(licenseId: number): Observable<LicenseSamlAuthenticationSettingsSimple> {
    return this.licensesApiService.getSamlAuthenticationSettings$(licenseId);
  }

  getSamlAuthenticationSettingsAndDefaultTeams$(licenseId: number): Observable<LicenseSamlAuthenticationSettings> {
    return this.licensesApiService.getSamlAuthenticationSettingsAndDefaultTeams$(licenseId);
  }

  getLicenseByDomain$(): Observable<LicensePublicModel> {
    return this.licensesApiService.getLicenseByDomain$().pipe(
      tap(license => this.addItems$([license]))
    );
  }
}
